import { Bot, Wrench } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useApp } from "@/lib/AppContext";
import { ApiError, type AgentCall, type ConnectedAgent } from "@/lib/api";
import { formatDate, formatRelative } from "@/lib/format";

/**
 * Settings → Connected agents (SEC-7, M9.3).
 *
 * **The consent screen promises this** — *"you can disconnect it at any time"* — and this section
 * is what makes it true. An agent that can approve an insight or re-run a step has to be one the
 * owner can see and disconnect.
 *
 * **No design to follow, and one to borrow from.** The handoff deliberately dropped its
 * "Agents & MCP" section, so there is nothing to satisfy here. This does for an agent exactly
 * what Settings → Recorder does for a recorder PC — list what is trusted, say what each may do,
 * and revoke — because that is the pattern the design already contains for "a thing this
 * account trusts", and inventing a second one would be worse than reusing it.
 *
 * **It says what an agent may do in the words the consent screen used**, not in scope strings.
 * `factorium:act` tells a person nothing; "can answer things on your behalf" tells them what
 * they are deciding about.
 */
export function AgentSettings({ summary }: { summary: string }) {
  const { api } = useApp();

  // null is "not loaded yet" and [] is "genuinely none" — two different things to say, and
  // the same distinction every other settings list draws.
  const [agents, setAgents] = useState<ConnectedAgent[] | null>(null);
  const [available, setAvailable] = useState(true);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setAgents(await api.listAgents());
      setAvailable(true);
    } catch (cause: unknown) {
      // A deployment with no MCP server maps no MCP routes at all, so a 404 here means "this
      // instance exposes nothing to agents" rather than "something went wrong" — the same
      // answer the chat surface gives to the same 404.
      if (cause instanceof ApiError && cause.status === 404) {
        setAvailable(false);
        setAgents([]);
        return;
      }

      setProblem(
        cause instanceof ApiError ? cause.message : "The connected agents could not be loaded.",
      );
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function disconnect(clientId: string) {
    if (busy) return;

    setBusy(true);
    setProblem(null);

    try {
      await api.revokeAgent(clientId);
      await refresh();
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "That agent could not be disconnected.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-[860px] flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header>
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Connected agents
        </h1>
        <p className="mt-1 text-[length:var(--text-md)] text-[var(--text-secondary)]">{summary}</p>
      </header>

      {problem && (
        <p
          role="alert"
          className="rounded-[var(--radius-lg)] border border-[var(--warning)] bg-[var(--surface-glass)] px-[var(--space-4)] py-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--text-primary)]"
        >
          {problem}
        </p>
      )}

      <section className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-6)] backdrop-blur-[var(--blur-sm)]">
        {!available ? (
          <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
            This deployment does not expose anything to agents, so there is nothing to connect
            to and nothing to disconnect.
          </p>
        ) : agents === null ? (
          <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
        ) : agents.length === 0 ? (
          <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
            No agent is connected. One appears here after you approve it — an agent sends you to
            a consent screen, and what you allow it there is what it can do.
          </p>
        ) : (
          <ul className="flex flex-col gap-[var(--space-4)]">
            {agents.map((agent) => (
              <li
                key={agent.clientId}
                className="flex items-start gap-[var(--space-4)] border-b border-[var(--border-subtle)] pb-[var(--space-4)] last:border-b-0 last:pb-0"
              >
                <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-[var(--primary)]/12 text-[var(--primary)]">
                  {agent.scopes.includes("factorium:ops") ? (
                    <Wrench className="size-4" aria-hidden />
                  ) : (
                    <Bot className="size-4" aria-hidden />
                  )}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="text-[length:var(--text-md)] font-semibold text-[var(--text-primary)]">
                    {agent.clientName}
                  </p>
                  <p className="mt-0.5 text-[length:var(--text-sm)] text-[var(--text-secondary)]">
                    {describe(agent.scopes)}
                  </p>
                  <p className="mt-1 text-[length:var(--text-xs)] text-[var(--text-muted)]">
                    Connected {formatDate(agent.connectedAt)} · last renewed{" "}
                    {formatDate(agent.lastUsedAt)}
                  </p>

                  <AgentActivity clientId={agent.clientId} />
                </div>

                <button
                  type="button"
                  onClick={() => void disconnect(agent.clientId)}
                  disabled={busy}
                  className="shrink-0 rounded-[var(--radius-md)] border border-[var(--border-default)] px-[var(--space-4)] py-[var(--space-2)] text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)] transition hover:bg-[var(--surface-2)] disabled:opacity-60"
                >
                  Disconnect
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/*
        Said rather than glossed over. Disconnecting revokes the refresh tokens, so the agent
        cannot renew — but an access token already in its hand stays valid until it expires.
        Promising an instant this cannot deliver would be worse than naming the half hour.
      */}
      {agents !== null && agents.length > 0 && (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          Disconnecting stops an agent renewing its access. Anything it is holding right now
          stops working within half an hour.
        </p>
      )}
    </div>
  );
}

/**
 * What this agent has actually done (`[MCP-CORE-6]`, M9.6).
 *
 * **Behind a disclosure, and fetched only when it is opened.** The list above answers "what may
 * this agent do", which is what somebody came here for; this answers "what has it been doing",
 * which is a question they ask about one agent, usually because they are already suspicious.
 * Loading every agent's history to draw a row nobody opened would spend a query per agent on
 * something almost never read.
 *
 * **The refusals are the point.** `mcp_calls` records what was allowed *and* what was turned
 * away, because an audit that logs only successes cannot answer the question it exists for —
 * and an agent reaching for something nobody granted it is visible here and nowhere else. So a
 * refused row is coloured and named rather than left to be spotted in a list of paths.
 *
 * A path and never a query string: this says which capability the credential used, not what was
 * said through it. A search's words are the owner's own and are not in this table.
 */
function AgentActivity({ clientId }: { clientId: string }) {
  const { api } = useApp();

  const [open, setOpen] = useState(false);
  const [calls, setCalls] = useState<AgentCall[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!open || calls !== null) return;

    void (async () => {
      try {
        setCalls(await api.listAgentCalls(clientId, 20));
      } catch (cause: unknown) {
        setProblem(
          cause instanceof ApiError ? cause.message : "That agent's activity could not be loaded.",
        );
      }
    })();
  }, [api, calls, clientId, open]);

  return (
    <div className="mt-[var(--space-2)]">
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        className="text-[length:var(--text-xs)] font-semibold text-[var(--primary)] underline-offset-2 hover:underline"
      >
        {open ? "Hide what it has done" : "What has it done?"}
      </button>

      {open && (
        <div className="mt-[var(--space-2)]">
          {problem ? (
            <p role="alert" className="text-[length:var(--text-xs)] text-[var(--text-secondary)]">
              {problem}
            </p>
          ) : calls === null ? (
            <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">Loading…</p>
          ) : calls.length === 0 ? (
            <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
              This agent has not called anything yet.
            </p>
          ) : (
            <ul className="flex flex-col gap-[var(--space-1)]">
              {calls.map((call) => (
                <li
                  key={`${call.createdAt}-${call.method}-${call.path}`}
                  className="flex items-baseline gap-[var(--space-2)] text-[length:var(--text-xs)]"
                >
                  <span
                    className={
                      call.outcome === "refused"
                        ? "font-semibold text-[var(--warning)]"
                        : "text-[var(--text-muted)]"
                    }
                  >
                    {call.outcome === "refused" ? "Refused" : call.method}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-mono text-[var(--text-secondary)]">
                    {call.path}
                  </span>
                  <span className="shrink-0 text-[var(--text-muted)]">
                    {formatRelative(call.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * What an agent may do, in the same words the consent screen asked for it in.
 *
 * Derived from the scopes rather than written per agent, so this sentence and the one somebody
 * approved cannot drift apart.
 */
function describe(scopes: string[]): string {
  const parts: string[] = [];

  if (scopes.includes("factorium:read")) parts.push("reads your knowledge");
  if (scopes.includes("factorium:act")) parts.push("answers things on your behalf");
  if (scopes.includes("factorium:ops")) parts.push("can inspect and re-run the processing");

  const last = parts.pop();
  if (last === undefined) return "Holds no permissions.";

  const sentence = parts.length === 0 ? last : `${parts.join(", ")} and ${last}`;

  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}
